import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import Navigation from '../../home/components/Navigation';

const HybridContentPage = () => {
  const features = [
    {
      icon: 'ri-magic-line',
      title: 'Seamless Integration',
      description: 'Perfect blend of AI imagery and video content'
    },
    {
      icon: 'ri-brush-line',
      title: 'Creative Freedom',
      description: 'Unlimited possibilities combining both mediums'
    },
    {
      icon: 'ri-flashlight-line',
      title: 'Dynamic Effects',
      description: 'Stunning transitions and visual effects'
    },
    {
      icon: 'ri-layout-masonry-line',
      title: 'Multi-Format',
      description: 'Optimized for all platforms and devices'
    },
    {
      icon: 'ri-stack-line',
      title: 'Layered Composition',
      description: 'Complex multi-layer visual storytelling'
    },
    {
      icon: 'ri-rocket-line',
      title: 'Innovation',
      description: 'Cutting-edge techniques for unique content'
    }
  ];

  const applications = [
    'Interactive Marketing Campaigns',
    'Immersive Brand Experiences',
    'Product Launch Videos',
    'Social Media Campaigns',
    'Event Presentations',
    'Educational Content'
  ];

  return (
    <div className="min-h-screen bg-black text-white">
      <Navigation />
      
      {/* Hero Section */}
      <section className="relative pt-40 pb-32 overflow-hidden">
        <div className="absolute inset-0">
          <img
            src="https://public.readdy.ai/ai/img_res/86d580add5a12ef75b666b427fd6b262.jpg"
            alt="Hybrid AI Content"
            className="w-full h-full object-cover object-center"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/60 via-black/40 to-black/80"></div>
        </div>
        
        <div className="relative max-w-[1400px] mx-auto px-12">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="max-w-4xl"
          >
            <div className="inline-flex items-center gap-3 px-6 py-3 rounded-full bg-gradient-to-r from-brand-pink/20 to-brand-aqua/20 backdrop-blur-xl border border-brand-pink/30 mb-8">
              <i className="ri-magic-line text-brand-pink text-xl"></i>
              <span className="text-sm font-semibold text-white">Hybrid AI Content</span>
            </div>
            
            <h1 className="text-7xl font-light mb-8">
              <span className="text-white">Enhance Your Content </span>
              <br />
              <span className="bg-gradient-to-r from-brand-pink to-brand-aqua bg-clip-text text-transparent">With AI Power</span>
            </h1>
            
            <p className="text-2xl text-white/80 leading-relaxed mb-12">
              Elevate your existing content with AI-powered enhancements—from audio improvement 
              and visual augmentation to intelligent content optimization and creative support.
            </p>

            <div className="flex gap-6">
              <Link to="/contact">
                <motion.button
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  className="px-10 py-4 bg-gradient-to-r from-brand-gold to-brand-teal rounded-full text-black font-bold whitespace-nowrap cursor-pointer"
                >
                  Get Started
                </motion.button>
              </Link>
              <Link to="/services">
                <motion.button
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  className="px-10 py-4 border-2 border-white/20 text-white rounded-full font-semibold whitespace-nowrap cursor-pointer hover:border-brand-gold/50 transition-colors"
                >
                  All Services
                </motion.button>
              </Link>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Features Grid */}
      <section className="py-32 bg-gradient-to-b from-transparent via-brand-gold/5 to-brand-teal/5">
        <div className="max-w-[1400px] mx-auto px-12">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-20"
          >
            <h2 className="text-6xl font-light text-white mb-6">
              Hybrid Innovation
            </h2>
            <p className="text-xl text-white/60 max-w-2xl mx-auto">
              Combining the best of AI imagery and cinematic video
            </p>
          </motion.div>

          <div className="grid grid-cols-3 gap-8">
            {features.map((feature, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                whileHover={{ y: -10 }}
                className="p-8 rounded-2xl bg-gradient-to-br from-zinc-900 to-black border border-white/10 cursor-pointer group"
              >
                <div className="w-16 h-16 flex items-center justify-center rounded-xl bg-gradient-to-br from-brand-gold/20 to-brand-teal/20 border border-brand-gold/30 mb-6 group-hover:scale-110 transition-transform">
                  <i className={`${feature.icon} text-3xl bg-gradient-to-r from-brand-gold to-brand-teal bg-clip-text text-transparent`}></i>
                </div>
                <h3 className="text-2xl font-semibold text-white mb-4">
                  {feature.title}
                </h3>
                <p className="text-white/60 leading-relaxed">
                  {feature.description}
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Applications */}
      <section className="py-32">
        <div className="max-w-[1400px] mx-auto px-12">
          <div className="grid grid-cols-2 gap-20 items-center">
            <motion.div
              initial={{ opacity: 0, x: -50 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <h2 className="text-6xl font-light text-white mb-8">
                Perfect Applications
              </h2>
              <div className="space-y-4">
                {applications.map((app, index) => (
                  <motion.div
                    key={index}
                    initial={{ opacity: 0, x: -20 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    viewport={{ once: true }}
                    transition={{ delay: index * 0.1 }}
                    className="flex items-center gap-4 p-4 rounded-xl bg-white/5 border border-white/10 hover:border-brand-gold/50 transition-colors cursor-pointer"
                  >
                    <div className="w-2 h-2 rounded-full bg-gradient-to-r from-brand-gold to-brand-teal" />
                    <span className="text-lg text-white/80">{app}</span>
                  </motion.div>
                ))}
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 50 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="relative"
            >
              <div className="grid grid-cols-2 gap-6">
                {[1, 2, 3, 4].map((i) => (
                  <motion.div
                    key={i}
                    whileHover={{ scale: 1.05 }}
                    className="relative h-64 rounded-2xl overflow-hidden cursor-pointer"
                  >
                    <img
                      src={`https://readdy.ai/api/search-image?query=Hybrid%20content%20example%20blending%20AI%20imagery%20with%20video%20elements%2C%20golden%20teal%20color%20palette%2C%20innovative%20visual%20design%2C%20professional%20quality%2C%208k%20resolution&width=400&height=500&seq=hybrid-ex-${i}&orientation=portrait`}
                      alt={`Example ${i}`}
                      className="w-full h-full object-cover object-top"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent" />
                  </motion.div>
                ))}
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Process */}
      <section className="py-32 bg-gradient-to-b from-transparent to-brand-teal/5">
        <div className="max-w-[1400px] mx-auto px-12">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-20"
          >
            <h2 className="text-6xl font-light text-white mb-6">
              Our Process
            </h2>
          </motion.div>

          <div className="grid grid-cols-4 gap-8">
            {[
              { step: '01', title: 'Concept', desc: 'Define vision and goals' },
              { step: '02', title: 'Create', desc: 'Generate AI assets' },
              { step: '03', title: 'Blend', desc: 'Integrate with video' },
              { step: '04', title: 'Deliver', desc: 'Final polished content' }
            ].map((item, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="text-center"
              >
                <div className="text-6xl font-light bg-gradient-to-r from-brand-gold to-brand-teal bg-clip-text text-transparent mb-6">
                  {item.step}
                </div>
                <h3 className="text-2xl font-semibold text-white mb-3">
                  {item.title}
                </h3>
                <p className="text-white/60">
                  {item.desc}
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-32">
        <div className="max-w-[1400px] mx-auto px-12">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="relative rounded-3xl overflow-hidden bg-gradient-to-br from-brand-gold/10 to-brand-teal/10 border border-brand-gold/20 p-20 text-center"
          >
            <h2 className="text-5xl font-light text-white mb-6">
              Ready to Create Hybrid Magic?
            </h2>
            <p className="text-xl text-white/70 mb-10 max-w-2xl mx-auto">
              Let's push the boundaries with innovative hybrid content.
            </p>
            <Link to="/contact">
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                className="px-12 py-5 bg-gradient-to-r from-brand-gold to-brand-teal rounded-full text-black font-bold text-lg whitespace-nowrap cursor-pointer"
              >
                Start Your Project
              </motion.button>
            </Link>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default HybridContentPage;